import {Player} from '@lottiefiles/react-lottie-player';
import React, {useEffect} from 'react';
import {Box, styled} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
} from '@lightricks/react-design-system';
import LinearProgress, {
  LinearProgressAppearance,
  LinearProgressProps,
  LinearProgressVariant,
} from '@contentlab/components/linear-progress';

const LOTTIE_LOGO =
  '/assets/content-lab/lotties/ltx-scaler-logo-animated-1.json';
const LOGO_SIZE = 112;
const DOT_DELAY = 600;
const DEFAULT_STEP_DURATION = 2500;
const LOGO_TITLE_CORRECTION = 6;

const TypingDotsContainer = styled('span')(() => ({
  'display': 'inline-block',
  'width': '3ch',
  'minWidth': '3ch',
  '@keyframes dot1': {
    '0%': {opacity: 0},
    '20%': {opacity: 1},
    '100%': {opacity: 1},
  },
  '@keyframes dot2': {
    '0%': {opacity: 0},
    '20%': {opacity: 0},
    '40%': {opacity: 1},
    '100%': {opacity: 1},
  },
  '@keyframes dot3': {
    '0%': {opacity: 0},
    '40%': {opacity: 0},
    '60%': {opacity: 1},
    '100%': {opacity: 1},
  },
}));

const AnimatedTitle = styled('span')(() => ({
  display: 'inline-block',
  transition: 'opacity 200ms ease-in-out',
}));

const TitleContainer = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  transform: 'translateX(12px)',
}));

function TypingDots() {
  const dots = [1, 2, 3];

  return (
    <TypingDotsContainer>
      {dots.map((dotNumber) => (
        <span
          key={dotNumber}
          style={{
            display: 'inline-block',
            animation: `dot${dotNumber} ${DOT_DELAY * 4}ms infinite`,
          }}
        >
          .
        </span>
      ))}
    </TypingDotsContainer>
  );
}

function AnimatedTitleText({
  titleText,
  stepDuration = DEFAULT_STEP_DURATION,
}: {
  titleText: string | string[];
  stepDuration?: number;
}) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(true);

  useEffect(() => {
    if (Array.isArray(titleText) && titleText.length > 1) {
      let intervalId: NodeJS.Timeout;
      let timeoutId: NodeJS.Timeout;

      const scheduleNext = () => {
        intervalId = setInterval(() => {
          setCurrentIndex((prev) => {
            const nextIndex = prev + 1;
            if (nextIndex >= titleText.length) {
              // Stop the interval when we reach the last item
              clearInterval(intervalId);
              return titleText.length - 1;
            }

            // Fade out, change text, fade in
            setIsVisible(false);
            timeoutId = setTimeout(() => {
              setCurrentIndex(nextIndex);
              setIsVisible(true);
            }, 200);

            return prev; // Keep current index during transition
          });
        }, stepDuration);
      };

      scheduleNext();

      return () => {
        clearInterval(intervalId);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
    return () => {};
  }, [titleText, stepDuration]);

  const displayText = Array.isArray(titleText)
    ? titleText[currentIndex]
    : titleText;

  return (
    <AnimatedTitle style={{opacity: isVisible ? 1 : 0}}>
      {displayText}
      <TypingDots key={displayText} />
    </AnimatedTitle>
  );
}

export interface LtxScalerFeatureLoaderProps {
  testID?: string;
  titleText: string | string[];
  subtitleText?: string;
  footerText?: string;
  loaderVariant?: LinearProgressProps['variant'];
  loaderProgress?: number;
  stepDuration?: number;
}

const Container = styled(Box)(() => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
}));

const ContentCard = styled(Box)(() => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  position: 'relative',
  width: '100%',
}));

const ContentContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  transform: `translateY(-${LOGO_TITLE_CORRECTION}px)`, // Move content up to reduce gap
}));

const LogoContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center',
  width: '100%',
  transform: `translateY(${LOGO_TITLE_CORRECTION}px)`, // Move logo down to reduce gap
}));

const AnimatedLogoContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

const ProgressContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '12px',
  width: '100%',
}));

const MainContentWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
}));

function LtxScalerFeatureLoader({
  testID = 'ltx-scaler-feature-loader',
  titleText,
  subtitleText,
  footerText,
  loaderVariant = LinearProgressVariant.INDETERMINATE,
  loaderProgress,
  stepDuration = 2000,
}: LtxScalerFeatureLoaderProps) {
  return (
    <Container data-testid={testID}>
      <ContentCard>
        <MainContentWrapper>
          <LogoContainer>
            <AnimatedLogoContainer>
              <Player
                src={LOTTIE_LOGO}
                autoplay
                loop
                style={{width: LOGO_SIZE, height: LOGO_SIZE}}
              />
            </AnimatedLogoContainer>
          </LogoContainer>

          <ContentContainer>
            <Headline
              size="xs"
              color={designSystemToken('semantic.fg.primary')}
            >
              <TitleContainer>
                <AnimatedTitleText
                  titleText={titleText}
                  stepDuration={stepDuration}
                />
              </TitleContainer>
            </Headline>
            {subtitleText ? (
              <Body
                size="md"
                color={designSystemToken('semantic.fg.secondary')}
              >
                {subtitleText}
              </Body>
            ) : null}

            <ProgressContainer>
              <LinearProgress
                variant={loaderVariant}
                appearance={LinearProgressAppearance.BRAND}
                width={200}
                height={4}
                showPercentage={
                  loaderVariant !== LinearProgressVariant.INDETERMINATE
                }
                value={loaderProgress}
              />
            </ProgressContainer>
          </ContentContainer>
        </MainContentWrapper>
      </ContentCard>
      {footerText ? (
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {footerText}
        </Body>
      ) : null}
    </Container>
  );
}

export default LtxScalerFeatureLoader;
